import {
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  type TextareaHTMLAttributes,
  useId,
} from 'react';

import { cx } from '@/ui/classNames';

import styles from './Form.module.css';

interface FieldChrome {
  readonly label: string;
  readonly optionalLabel?: string;
  readonly hint?: string;
  readonly error?: string | null;
  readonly counter?: string;
}

interface ControlAria {
  readonly id: string;
  readonly 'aria-invalid': true | undefined;
  readonly 'aria-describedby': string | undefined;
}

/**
 * Habillage commun : libellé au-dessus, aide et erreur reliées au champ par
 * `aria-describedby`, erreur annoncée aux lecteurs d'écran.
 */
function FieldShell({
  label,
  optionalLabel,
  hint,
  error,
  counter,
  className,
  renderControl,
}: FieldChrome & {
  readonly className?: string | undefined;
  readonly renderControl: (aria: ControlAria) => ReactNode;
}) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ');

  return (
    <div className={cx(styles.field, className)}>
      <div className={styles.labelRow}>
        <label className={styles.label} htmlFor={id}>
          {label}
          {optionalLabel && <span className={styles.optional}> ({optionalLabel})</span>}
        </label>
        {counter && (
          <span className={styles.counter} aria-live="polite">
            {counter}
          </span>
        )}
      </div>
      {renderControl({
        id,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': describedBy || undefined,
      })}
      {error && (
        <p id={errorId} className={styles.error} role="alert">
          {error}
        </p>
      )}
      {!error && hint && (
        <p id={hintId} className={styles.hint}>
          {hint}
        </p>
      )}
    </div>
  );
}

export function TextInput({
  label,
  optionalLabel,
  hint,
  error,
  counter,
  className,
  ref,
  ...inputProps
}: FieldChrome & InputHTMLAttributes<HTMLInputElement> & { readonly ref?: Ref<HTMLInputElement> }) {
  return (
    <FieldShell
      {...{ label, optionalLabel, hint, error, counter, className }}
      renderControl={(aria) => (
        <input ref={ref} className={styles.control} {...inputProps} {...aria} />
      )}
    />
  );
}

export function TextArea({
  label,
  optionalLabel,
  hint,
  error,
  counter,
  className,
  ref,
  ...textareaProps
}: FieldChrome &
  TextareaHTMLAttributes<HTMLTextAreaElement> & { readonly ref?: Ref<HTMLTextAreaElement> }) {
  return (
    <FieldShell
      {...{ label, optionalLabel, hint, error, counter, className }}
      renderControl={(aria) => (
        <textarea
          ref={ref}
          className={cx(styles.control, styles.textarea)}
          {...textareaProps}
          {...aria}
        />
      )}
    />
  );
}
